import { FC, useMemo, useState } from 'react';
import {
  Badge,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  PopoverVirtualElement,
} from '@mui/material';
import {
  IconCircleCheck,
  IconCopy,
  IconCpu,
  IconDotsVertical,
  IconExternalLink,
  IconHelpCircle,
  IconPackage,
} from '@tabler/icons-react';
import { Part, PartCore } from 'types/part';
import { StockLotCRUDSchema, StockLotLite } from 'types/inventory';
import Avatar, { AvatarSize } from 'ui-component/extended/Avatars/Avatar';
import { useNavigate } from 'react-router-dom';
import { copyClipboard } from 'utils/functions';
import { ALL_APPS } from 'constants/appConstants';
import { PropertySubsetValidator, UUID } from 'types/api';
import NoPartAvatar from 'ui-component/extended/Avatars/NoPartAvatar';
import { BASE_WAREHOUSE_PATH } from 'routes/constants';
import _ from 'lodash';
import {
  OrgPartIcons,
  PublicPartSchema,
  PublicPartSchemaLite,
  OrgPartSchema,
  OrgPartSchemaLite,
  PublicPartSchemaSearch,
} from 'types/clientV2/parts';
import useCreateLinkToRecord from 'hooks/useCreateLinkToRecord';

// Type guard to check if part has partType field
// Because PartCore doesn't have partType field
export function hasPartType(
  part:
    | Part
    | PartCore
    | PublicPartSchema
    | PublicPartSchemaLite
    | PublicPartSchemaSearch
    | OrgPartSchema
    | OrgPartSchemaLite
    | null
): part is Part & { partType: string } {
  return (
    part != null &&
    'partType' in part &&
    typeof (part as Part).partType === 'string' &&
    (part as Part).partType !== ''
  );
}

interface PartAvatarProps {
  part:
    | Part
    | PartCore
    | PublicPartSchema
    | PublicPartSchemaLite
    | PublicPartSchemaSearch
    | OrgPartSchema
    | OrgPartSchemaLite
    | null;
  altCount?: number | null;
  unapprovedAltCount?: number | null;
  stockLot?:
    | PropertySubsetValidator<StockLotCRUDSchema | StockLotLite, 'id'>
    | null
    | StockLotCRUDSchema['id']
    | StockLotLite['id'];
  searchParams?: string;
  size?: AvatarSize;
  v2PartUUID?: UUID | null;
}

const PartAvatar: FC<PartAvatarProps> = ({
  part,
  altCount,
  unapprovedAltCount = undefined,
  stockLot,
  searchParams,
  size = AvatarSize.MD,
  v2PartUUID,
}) => {
  const navigate = useNavigate();

  const { createLinkWithActiveOrgId } = useCreateLinkToRecord();

  const forWarehouse = window.location.pathname.includes(BASE_WAREHOUSE_PATH);

  const [anchorEl, setAnchorEl] = useState<
    Element | PopoverVirtualElement | null
  >(null);

  const handleClick = (event: React.SyntheticEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const partLink = useMemo(() => {
    let partLinkString = '';
    if (v2PartUUID) {
      partLinkString = `${ALL_APPS.PARTS.path}/${v2PartUUID}`;
    } else if (part && searchParams) {
      partLinkString = `${ALL_APPS.PART_SEARCH.path}/${part?.id}?${searchParams}`;
    } else if (part) {
      partLinkString = `${ALL_APPS.PART_SEARCH.path}/${part?.id}`;
    }
    return partLinkString;
  }, [v2PartUUID, searchParams, part]);

  const stockLotLink = useMemo(() => {
    let stockLotLinkString = '';
    if (stockLot && searchParams) {
      stockLotLinkString = `${ALL_APPS.STOCK.views?.STOCK_LOTS?.path}/${
        typeof stockLot === 'string' ? stockLot : stockLot?.id
      }?${searchParams}`;
    } else if (stockLot) {
      stockLotLinkString = `${ALL_APPS.STOCK.views?.STOCK_LOTS?.path}/${
        typeof stockLot === 'string' ? stockLot : stockLot?.id
      }`;
    }
    return stockLotLinkString;
  }, [stockLot, searchParams]);

  const PartTypeIcon = hasPartType(part)
    ? _.get(OrgPartIcons, part.partType, IconCpu)
    : IconCpu;

  if (!part?.id) {
    return <NoPartAvatar size={size} />;
  }

  return (
    <>
      <Badge
        badgeContent={
          altCount ? (
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
            >
              <IconCircleCheck width="14px" height="14px" />
              <Typography variant="inherit">
                {altCount.toLocaleString()}
              </Typography>
            </Stack>
          ) : (
            0
          )
        }
        color="success"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '.MuiBadge-anchorOriginBottomLeft': {
            transform: 'none',
            bottom: '3px',
            left: '3px',
            visibility: altCount ? 'visible' : 'hidden',
          },
        }}
      >
        <Badge
          badgeContent={
            unapprovedAltCount ? (
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={0.5}
              >
                <IconHelpCircle width="14px" height="14px" />
                <Typography variant="inherit">
                  {unapprovedAltCount.toLocaleString()}
                </Typography>
              </Stack>
            ) : (
              0
            )
          }
          color="secondary"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{
            '.MuiBadge-anchorOriginBottomLeft': {
              transform: 'none',
              bottom: altCount ? '26px' : '3px',
              left: '3px',
              visibility: unapprovedAltCount ? 'visible' : 'hidden',
            },
          }}
        >
          {part?.heroImage ? (
            <Avatar
              src={part?.heroImage}
              size={size}
              variant="rounded"
              sx={{
                '& img': {
                  objectFit: 'contain',
                },
              }}
            >
              <PartTypeIcon />
            </Avatar>
          ) : (
            <Avatar size={size} variant="rounded">
              <PartTypeIcon />
            </Avatar>
          )}
          <Tooltip
            title="More options"
            sx={{
              position: 'absolute',
              top: '0px',
              right: '0px',
            }}
          >
            {/* TODO: figure out how to correctly swap to SegmentIconButton -- onClicks weren't properly working when swapping over */}
            <IconButton
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                handleClick(event);
              }}
              size="small"
              color="secondary"
              disabled={!part}
            >
              <IconDotsVertical />
            </IconButton>
          </Tooltip>
        </Badge>
      </Badge>
      {Boolean(anchorEl) && (
        <Menu
          id="menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          variant="selectedMenu"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {!forWarehouse && (
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                navigate(partLink);
                handleClose();
              }}
            >
              <ListItemIcon>
                <IconCpu />
              </ListItemIcon>
              <ListItemText>Go To Part</ListItemText>
            </MenuItem>
          )}
          {!forWarehouse && (
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                window.open(`${window.location.origin}${partLink}`, '_blank');
                handleClose();
              }}
            >
              <ListItemIcon>
                <IconExternalLink />
              </ListItemIcon>
              <ListItemText>Open Part In New Tab</ListItemText>
            </MenuItem>
          )}
          {!forWarehouse && (
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                copyClipboard(
                  createLinkWithActiveOrgId(
                    `${window.location.origin}${partLink}`
                  )
                );
                handleClose();
              }}
            >
              <ListItemIcon>
                <IconCopy />
              </ListItemIcon>
              <ListItemText>Copy Link to Part</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              if (_.get(part, 'cofactrId', null)) {
                copyClipboard(_.get(part, 'cofactrId'));
              } else {
                copyClipboard(part.id);
              }
              handleClose();
            }}
          >
            <ListItemIcon>
              <IconCopy />
            </ListItemIcon>
            <ListItemText>Copy Cofactr ID</ListItemText>
          </MenuItem>
          {stockLotLink && [
            <Divider key="divider" />,
            <MenuItem
              key="goToStockLot"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                navigate(stockLotLink);
                handleClose();
              }}
            >
              <ListItemIcon>
                <IconPackage />
              </ListItemIcon>
              <ListItemText>Go To Stock Lot</ListItemText>
            </MenuItem>,
            <MenuItem
              key="openStockLot"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                window.open(
                  `${window.location.origin}${stockLotLink}`,
                  '_blank'
                );
                handleClose();
              }}
            >
              <ListItemIcon>
                <IconExternalLink />
              </ListItemIcon>
              <ListItemText>Open Stock Lot In New Tab</ListItemText>
            </MenuItem>,
            <MenuItem
              key="copyLinkToStockLot"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                copyClipboard(
                  createLinkWithActiveOrgId(
                    `${window.location.origin}${stockLotLink}`
                  )
                );
                handleClose();
              }}
            >
              <ListItemIcon>
                <IconCopy />
              </ListItemIcon>
              <ListItemText>Copy Link to Stock Lot</ListItemText>
            </MenuItem>,
          ]}
        </Menu>
      )}
    </>
  );
};

export default PartAvatar;
